import { api } from '../../../../lib/api';

export const LABOR_CALENDAR_VIEWS = {
  FACTORY: 'factory',
  PATTERN: 'pattern',
  EMPLOYEE: 'employee',
  TEAM: 'team',
};

/** Mensaje del API cuando no hay MrWorkingCalendar para factory_id + year */
export const LABOR_NO_CALENDAR_ERROR = 'no calendar found';

function isNoCalendarError(error) {
  const msg = error?.message || '';
  return msg.toLowerCase().includes(LABOR_NO_CALENDAR_ERROR);
}

export const laborScheduleService = {
  async getEmployeeYearSchedule(employeeId, year) {
    const params = new URLSearchParams({
      employee_id: String(employeeId),
      year: String(year),
    });
    const data = await api.get(`/api/labor/summary/employees/year-schedule?${params}`);
    return Array.isArray(data) ? data : [];
  },

  async getPatternYearSchedule(patternId, year, cycleStart = null) {
    const params = new URLSearchParams({
      pattern_id: String(patternId),
      year: String(year),
    });
    if (cycleStart) {
      params.set('cycle_start', cycleStart);
    }
    const data = await api.get(`/api/labor/summary/patterns/year-schedule?${params}`);
    return Array.isArray(data) ? data : [];
  },

  async getFactoryYearSchedule(factoryId, year) {
    const params = new URLSearchParams({
      factory_id: String(factoryId),
      year: String(year),
    });
    try {
      const data = await api.get(`/api/labor/summary/factories/year-schedule?${params}`);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      // El backend devuelve 500 con "no calendar found for factory X year Y" si no existe calendario
      if (isNoCalendarError(error)) {
        return [];
      }
      throw error;
    }
  },

  async getEmployeeDaySchedule(employeeId, date) {
    const params = date ? `?date=${date}` : '';
    return api.get(`/api/labor/calendars/employees/schedule/${employeeId}${params}`);
  },

  /**
   * Lista calendarios de una fábrica (opcionalmente filtrados por año).
   * GET /api/labor/calendars?factory_id=&year=
   */
  async listCalendarsByFactory(factoryId, year = null) {
    const params = new URLSearchParams({ factory_id: String(factoryId) });
    if (year != null) {
      params.set('year', String(year));
    }
    const data = await api.get(`/api/labor/calendars?${params}`);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Crea un calendario laboral (MrWorkingCalendar) para fábrica + año.
   * POST /api/labor/calendars
   */
  async createCalendar({ factoryId, year, name, description = '', status = 'draft' }) {
    return api.post('/api/labor/calendars', {
      factory_id: Number(factoryId),
      year: Number(year),
      name: String(name).trim(),
      description,
      status,
    });
  },

  /**
   * Genera los días del año para un calendario existente.
   * POST /api/labor/calendars/generate/:cal_id?year=
   */
  async generateCalendarDays(calendarId, year) {
    const params = new URLSearchParams({ year: String(year) });
    return api.post(`/api/labor/calendars/generate/${calendarId}?${params}`, {});
  },

  /**
   * Lista festivos de un calendario.
   * GET /api/labor/calendars/holidays?calendar_id=
   */
  async listHolidaysByCalendar(calendarId) {
    const params = new URLSearchParams({ calendar_id: String(calendarId) });
    const data = await api.get(`/api/labor/calendars/holidays?${params}`);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Registra un festivo y actualiza el día del calendario.
   * POST /api/labor/calendars/holidays
   */
  async createHoliday({
    calendarId,
    date,
    name,
    holidayType = 'national',
    isRecoverable = false,
    isPaid = true,
  }) {
    return api.post('/api/labor/calendars/holidays', {
      calendar_id: Number(calendarId),
      date: `${date}T12:00:00.000Z`,
      name: String(name).trim(),
      holiday_type: holidayType,
      is_recoverable: Boolean(isRecoverable),
      is_paid: Boolean(isPaid),
    });
  },

  /**
   * Elimina un festivo y restaura el día del calendario.
   * DELETE /api/labor/calendars/holidays/:hol_id
   */
  async deleteHoliday(holidayId) {
    return api.delete(`/api/labor/calendars/holidays/${holidayId}`);
  },

  /**
   * Crea un patrón de trabajo ligado a un calendario de planta.
   * POST /api/labor/patterns
   */
  async createPattern({
    calendarId,
    name,
    code,
    description = '',
    isRotating = false,
    cycleDays = 0,
    cycleStartDate = null,
    active = true,
  }) {
    const body = {
      calendar_id: Number(calendarId),
      name: String(name).trim(),
      code: String(code).trim(),
      description,
      is_rotating: Boolean(isRotating),
      cycle_days: Number(cycleDays) || 0,
      active: Boolean(active),
    };
    if (cycleStartDate && isRotating) {
      body.cycle_start_date = `${cycleStartDate}T00:00:00.000Z`;
    }
    return api.post('/api/labor/patterns', body);
  },

  /**
   * Añade un día al patrón.
   * POST /api/labor/patterns/days-add/:patt_id
   */
  async addPatternDay(patternId, day) {
    const payload = {
      day_sequence: day.day_sequence,
      day_of_week: day.day_of_week ?? null,
      shift_code: day.shift_code,
      is_working_day: day.is_working_day !== false,
      start_time: day.start_time,
      end_time: day.end_time,
      break_minutes: day.break_minutes ?? null,
      total_hours: day.total_hours ?? null,
    };
    return api.post(`/api/labor/patterns/days-add/${patternId}`, payload);
  },

  /**
   * Crea patrón y todos sus días (secuencial).
   * @param {Function} [onProgress] - ({ current, total, label }) durante days-add
   */
  async createPatternWithDays({ pattern, days }, onProgress) {
    const created = await this.createPattern(pattern);
    const patternId = created?.id;
    if (!patternId) {
      throw new Error('El API no devolvió el ID del patrón creado.');
    }
    const total = days.length;
    for (let i = 0; i < total; i++) {
      const day = days[i];
      onProgress?.({
        current: i + 1,
        total,
        label: day.shift_code || `Secuencia ${day.day_sequence}`,
      });
      await this.addPatternDay(patternId, day);
    }
    return { pattern: created, patternId, daysCreated: total };
  },

  /**
   * Asigna un patrón de trabajo a un empleado.
   * POST /api/labor/patterns/assign-employees/:employeeId
   * (el parámetro de ruta es employee_id; pattern_id va en el body)
   */
  async assignPatternToEmployee(employeeId, { patternId, calendarId, startDate, endDate = null }) {
    const body = {
      pattern_id: Number(patternId),
      calendar_id: Number(calendarId),
      start_date: `${startDate}T00:00:00.000Z`,
    };
    if (endDate) {
      body.end_date = `${endDate}T00:00:00.000Z`;
    }
    return api.post(`/api/labor/patterns/assign-employees/${employeeId}`, body);
  },

  /**
   * Asigna un patrón de trabajo a todos los miembros activos de un equipo.
   * POST /api/labor/teams/:teamId/assign-pattern
   */
  async assignPatternToTeam(teamId, { patternId, calendarId, startDate, endDate = null }) {
    const body = {
      pattern_id: Number(patternId),
      calendar_id: Number(calendarId),
      start_date: `${startDate}T00:00:00.000Z`,
    };
    if (endDate) {
      body.end_date = `${endDate}T00:00:00.000Z`;
    }
    return api.post(`/api/labor/teams/${teamId}/assign-pattern`, body);
  },

  /**
   * Lista overrides de un calendario.
   * GET /api/labor/calendars/overrides/:cal_id?include_expired=
   */
  async listOverrides(calendarId, includeExpired = false) {
    const params = new URLSearchParams({
      include_expired: includeExpired ? 'true' : 'false',
    });
    const data = await api.get(
      `/api/labor/calendars/overrides/${calendarId}?${params}`
    );
    return Array.isArray(data) ? data : [];
  },

  /**
   * Crea un override (fábrica si employeeId es null, o empleado concreto).
   * POST /api/labor/calendars/overrides/:cal_id
   */
  async createOverride(calendarId, {
    employeeId = null,
    startDate,
    endDate,
    isWorkingDay = true,
    startTime = null,
    endTime = null,
    reason,
  }) {
    const body = {
      employee_id: employeeId != null && employeeId !== '' ? Number(employeeId) : null,
      start_date: `${startDate}T00:00:00.000Z`,
      end_date: `${endDate}T00:00:00.000Z`,
      is_working_day: Boolean(isWorkingDay),
      start_time: startTime || null,
      end_time: endTime || null,
      reason: String(reason).trim(),
    };
    return api.post(`/api/labor/calendars/overrides/${calendarId}`, body);
  },

  /**
   * Actualiza un override existente.
   * PUT /api/labor/calendars/overrides/:over_id
   */
  async updateOverride(overrideId, {
    employeeId = null,
    startDate,
    endDate,
    isWorkingDay = true,
    startTime = null,
    endTime = null,
    reason,
  }) {
    const body = {
      employee_id: employeeId != null && employeeId !== '' ? Number(employeeId) : null,
      start_date: `${startDate}T00:00:00.000Z`,
      end_date: `${endDate}T00:00:00.000Z`,
      is_working_day: Boolean(isWorkingDay),
      start_time: startTime || null,
      end_time: endTime || null,
      reason: String(reason).trim(),
    };
    return api.put(`/api/labor/calendars/overrides/${overrideId}`, body);
  },

  /**
   * Elimina un override.
   * DELETE /api/labor/calendars/overrides/:over_id
   */
  async deleteOverride(overrideId) {
    return api.delete(`/api/labor/calendars/overrides/${overrideId}`);
  },

  /**
   * Obtiene un patrón con sus días.
   * GET /api/labor/patterns/:patt_id
   */
  async getPattern(patternId) {
    return api.get(`/api/labor/patterns/${patternId}`);
  },

  /**
   * Actualiza metadatos y días de un patrón (reemplazo total de días).
   * PUT /api/labor/patterns/:patt_id
   */
  async updatePattern(patternId, {
    name,
    description = '',
    active = true,
    cycleStartDate = null,
    days = [],
  }) {
    const body = {
      name: String(name).trim(),
      description,
      active: Boolean(active),
      days: days.map((day) => ({
        day_sequence: day.day_sequence,
        day_of_week: day.day_of_week ?? null,
        shift_code: day.shift_code,
        is_working_day: day.is_working_day !== false,
        start_time: day.start_time,
        end_time: day.end_time,
        break_minutes: day.break_minutes ?? null,
        total_hours: day.total_hours ?? null,
      })),
    };
    if (cycleStartDate) {
      body.cycle_start_date = `${cycleStartDate}T00:00:00.000Z`;
    }
    return api.put(`/api/labor/patterns/${patternId}`, body);
  },

  /**
   * Elimina un patrón sin asignaciones ni fichajes asociados.
   * DELETE /api/labor/patterns/:patt_id
   */
  async deletePattern(patternId) {
    return api.delete(`/api/labor/patterns/${patternId}`);
  },

  /**
   * Lista los patrones de un calendario.
   * GET /api/labor/patterns?calendar_id=
   */
  async listPatternsByCalendar(calendarId) {
    const params = new URLSearchParams({ calendar_id: String(calendarId) });
    const data = await api.get(`/api/labor/patterns?${params}`);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Añade un requisito de habilidad a un patrón.
   * POST /api/labor/patterns/skill-requirements/:patt_id
   */
  async addPatternSkillRequirement(patternId, {
    skillId,
    minLevel = 'básico',
    requiredCount = 1,
    validFrom,
    validTo = null,
    reason = '',
  }) {
    const body = {
      skill_id: Number(skillId),
      min_level: minLevel,
      required_count: Number(requiredCount) || 1,
      valid_from: `${validFrom}T00:00:00.000Z`,
      reason: reason?.trim() || '',
    };
    if (validTo) {
      body.valid_to = `${validTo}T00:00:00.000Z`;
    }
    return api.post(`/api/labor/patterns/skill-requirements/${patternId}`, body);
  },

  /**
   * Comprueba cobertura de skills para un patrón en una fecha.
   * GET /api/labor/patterns/coverage-check/:patt_id?date=YYYY-MM-DD
   */
  async checkPatternCoverage(patternId, date) {
    const params = new URLSearchParams({ date: String(date) });
    return api.get(`/api/labor/patterns/coverage-check/${patternId}?${params}`);
  },

  /**
   * Asistencia planificada y real de un patrón en una fecha.
   * GET /api/labor/attendance/shifts/:patt_id?date=YYYY-MM-DD&shift_code=
   * shiftCode es opcional; si se omite devuelve todos los empleados del patrón.
   */
  async getShiftAttendance(patternId, date, shiftCode = '') {
    const params = new URLSearchParams({ date: String(date) });
    if (shiftCode) params.set('shift_code', shiftCode);
    return api.get(`/api/labor/attendance/shifts/${patternId}?${params}`);
  },

  /**
   * Asistencia planificada y real de un patrón en un rango de fechas.
   * GET /api/labor/attendance/shifts/:patt_id?from=YYYY-MM-DD&to=YYYY-MM-DD
   */
  async getShiftAttendanceRange(patternId, from, to, shiftCode = '') {
    const params = new URLSearchParams({
      from: String(from),
      to: String(to),
    });
    if (shiftCode) params.set('shift_code', shiftCode);
    return api.get(`/api/labor/attendance/shifts/${patternId}?${params}`);
  },

  /**
   * Asistencia del censo indicado. Cada fila trae su patrón, o ninguno si no está asignado.
   * POST /api/labor/attendance/roster
   */
  async getAttendanceRoster(employeeIds, from, to) {
    return api.post('/api/labor/attendance/roster', {
      employee_ids: employeeIds,
      from,
      to,
    });
  },

  /**
   * Patrón activo del empleado en una fecha.
   * GET /api/labor/employees/:employeeId/active-pattern?date=YYYY-MM-DD
   */
  async getEmployeeActivePattern(employeeId, date) {
    const params = new URLSearchParams({ date: String(date) });
    return api.get(`/api/labor/employees/${employeeId}/active-pattern?${params}`);
  },

  /**
   * Valida fichajes brutos y crea el registro en mr_attendances.
   * POST /api/labor/attendance/validate-from-raw
   */
  async validateAttendanceFromRaw({
    employeeId,
    date,
    patternId,
    action,
    notes = '',
    observations = '',
    validatorEmployeeId,
    clockIn = null,
    clockOut = null,
  }) {
    const body = {
      employee_id: Number(employeeId),
      date: String(date),
      pattern_id: patternId ? Number(patternId) : 0,
      action,
      notes,
      observations,
      validator_employee_id: Number(validatorEmployeeId),
    };
    if (clockIn) body.clock_in = clockIn;
    if (clockOut) body.clock_out = clockOut;
    return api.post('/api/labor/attendance/validate-from-raw', body);
  },

  /**
   * Aprueba o rechaza un registro de asistencia ya existente (legacy).
   * PUT /api/labor/attendance/:att_id/validate
   * @param {number} attendanceId
   * @param {{ action: 'approve'|'reject', notes?: string, validatorEmployeeId: number }} opts
   */
  async validateAttendance(attendanceId, { action, notes = '', validatorEmployeeId }) {
    return api.put(`/api/labor/attendance/${attendanceId}/validate`, {
      action,
      notes,
      validator_employee_id: Number(validatorEmployeeId),
    });
  },

  /**
   * Revierte un registro validado a estado pending.
   * PUT /api/labor/attendance/:att_id/revert
   * @param {number} attendanceId
   * @param {{ validatorEmployeeId: number }} opts
   */
  async revertAttendanceValidation(attendanceId, { validatorEmployeeId }) {
    return api.put(`/api/labor/attendance/${attendanceId}/revert`, {
      validator_employee_id: Number(validatorEmployeeId),
    });
  },

  /**
   * Aprueba en bloque empleados con fichajes brutos pendientes.
   * POST /api/labor/attendance/bulk-approve-from-raw
   */
  async bulkApproveAttendanceFromRaw({ employeeIds, date, patternId, validatorEmployeeId }) {
    return api.post('/api/labor/attendance/bulk-approve-from-raw', {
      employee_ids: employeeIds.map(Number),
      date: String(date),
      pattern_id: patternId ? Number(patternId) : 0,
      validator_employee_id: Number(validatorEmployeeId),
    });
  },

  /**
   * Detalle de tramos (entrada/salida) de un empleado en una fecha. Si el día ya está
   * validado devuelve el snapshot guardado; si no, lo calcula al vuelo desde los fichajes
   * brutos (para previsualizar antes de aprobar).
   * GET /api/labor/attendance/:employee_id/segments?date=YYYY-MM-DD
   */
  async getAttendanceSegments(employeeId, date) {
    const params = new URLSearchParams({ date: String(date) });
    return api.get(`/api/labor/attendance/${employeeId}/segments?${params}`);
  },
};
